import { FronteggContext } from '@frontegg/rest-api';
import { io } from 'socket.io-client';
import { analytics } from 'src/services';
let audioContext, socket, input, micStream, scriptNode;

let url = import.meta.env.VITE_AMBIENT_API_BASE_URL + '/listen';

// Add this near the top with other let declarations
let lastTranscriptTime = null;
const TRANSCRIPT_TIMEOUT_MS = 15000; // 15 seconds without transcription before showing error
let isTimeoutErrorShown = false; // Add this flag to track error state

export function doStream({
  encounterId,
  encounterPhase,
  setMicLevel,
  setLiveTranscript,
  setLivePartialTranscript,
  selectedMicrophoneId,
  successCallback,
  failureCallback,
  setDictation = () => {},
  isDictate = false,
  transcriptTimeoutCallback,
  user,
}) {
  let connectionEstablished = false;

  // Maximum number of times to attempt to reconnect if connection to deepgram/assemblyAI inside our socket.io server is lost.
  // Note that this is different from the "reconnectionAttempts" option, which is the number of times socketio automatically
  // attempts to reconnect due to network issues etc.
  const max_manual_reconnection_attempts = 5;
  const reconnection_window_ms = 5 * 60 * 1000; // 5 minutes in milliseconds
  let manual_reconnection_attempts = 0;
  let first_reconnection_attempt_time = null;

  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  audioContext.onstatechange = () => {
    if (audioContext.state === 'suspended') {
      analytics.track(`User's Microphone Context Is Suspended`, {
        email: user?.email,
      });
      failureCallback('AUDIO_CONTEXT_SUSPENDED');
    }
  };
  socket = io(url, {
    path: '/socket.io/',
    withCredentials: true,
    query: {
      encounter_id: encounterId,
      encounter_phase: encounterPhase,
      sample_rate: audioContext.sampleRate,
      is_dictate: isDictate,
    },
    extraHeaders: {
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    },
    reconnection: true, // Enable automatic reconnection
    reconnectionAttempts: 12, // Retry for about 1 minute.
    reconnectionDelay: 5000, // Fixed delay for reconnection (ms)
    reconnectionDelayMax: 5000, // Maximum delay between reconnections (ms)
    forceNew: true,
  });

  // Add timeout checker
  const checkTranscriptTimeout = () => {
    if (
      lastTranscriptTime &&
      Date.now() - lastTranscriptTime > TRANSCRIPT_TIMEOUT_MS
    ) {
      if (!isTimeoutErrorShown) {
        isTimeoutErrorShown = true;
        transcriptTimeoutCallback('NO_TRANSCRIPTION_RECEIVED');
      }
    }
    setTimeout(checkTranscriptTimeout, 1000); // Check every second
  };

  socket.on('connected', () => {
    connectionEstablished = true;
    if (!window.isMicOn) {
      onOpen(
        setMicLevel,
        selectedMicrophoneId,
        successCallback,
        failureCallback
      );
      lastTranscriptTime = Date.now();
      checkTranscriptTimeout();
    }
  });

  socket.on('transcript', (data) => {
    if (data.length > 0) {
      lastTranscriptTime = Date.now();
      if (isTimeoutErrorShown) {
        isTimeoutErrorShown = false;
        transcriptTimeoutCallback('ENABLED_TRANSCRIPTION_RECEIVED');
        successCallback(); // Use this to clear the error state
      }
    }
    if (isDictate) {
      setDictation(data);
    } else {
      setLiveTranscript(data);
    }
  });

  socket.on('partial_transcript', (data) => {
    if (data.length > 0) {
      lastTranscriptTime = Date.now();
      if (isTimeoutErrorShown) {
        isTimeoutErrorShown = false;
        transcriptTimeoutCallback('ENABLED_PARTIAL_TRANSCRIPTION_RECEIVED');
        successCallback(); // Use this to clear the error state
      }
    }
    if (!isDictate) {
      setLivePartialTranscript(data);
    }
  });

  socket.on('disconnect', (reason) => {
    console.error(
      `Socket is not connected; not sending audio until reconnected. ${reason}`
    );

    if (reason === 'io server disconnect') {
      const now = Date.now();
      // Initialize the first attempt time if it hasn't been set
      if (!first_reconnection_attempt_time) {
        first_reconnection_attempt_time = now;
      }

      // Check if we're still within the time window
      if (now - first_reconnection_attempt_time <= reconnection_window_ms) {
        if (manual_reconnection_attempts < max_manual_reconnection_attempts) {
          manual_reconnection_attempts++;
          console.log(
            `Attempt #${manual_reconnection_attempts} - Reconnecting to server...`
          );
          socket.connect();
        } else {
          console.error(
            `Maximum socket.io reconnection attempts of ${max_manual_reconnection_attempts} reached within 5 minute window`
          );
          analytics.track(
            `Maximum socket.io reconnection attempts of ${max_manual_reconnection_attempts} reached`,
            { email: user?.email }
          );
          failureCallback('MAX_RECONNECTIONS_EXCEEDED');
        }
      } else {
        // Reset counters if we're outside the time window
        manual_reconnection_attempts = 1;
        first_reconnection_attempt_time = now;
        console.log('Reconnection window reset - Attempting to reconnect...');
        socket.connect();
      }
    }
  });

  socket.on('connect_error', (error) => {
    if (!connectionEstablished) {
      console.error('Failed to connect to server:', error);
      failureCallback('NETWORK_INIT_ERROR');
    }
  });

  socket.io.on('reconnect_failed', () => {
    console.error('Given up on reconnecting to server.');
    failureCallback('NETWORK_ERROR');
  });
}

export function endStream() {
  lastTranscriptTime = null;
  window.isMicOn = false;

  if (scriptNode) {
    scriptNode.removeEventListener('audioprocess', processAudioEvent);
    scriptNode.disconnect();
  }

  if (input) {
    input.disconnect();
  }

  if (micStream) {
    micStream.getTracks().forEach((track) => track.stop());
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
  }

  if (socket) {
    socket.close();
  }
}

function calculateMicLevelPercent(inputData) {
  let i;
  let sum = 0.0;
  let instant = 0;

  for (i = 0; i < inputData.length; ++i) {
    sum += inputData[i] * inputData[i];
  }
  instant = Math.sqrt(sum / inputData.length);
  return Math.min(instant * 200 * 5, 100);
}

/**
 * Transform an audio processing event into a form suitable to be sent to the API. (S16LE or Signed 16 bit Little Edian).
 * Then send.
 * @param {AudioProcessingEvent} e
 */
function processAudioEvent(e, setMicLevel) {
  if (
    audioContext.state === 'suspended' ||
    audioContext.state === 'closed' ||
    !socket
  ) {
    console.error(
      'AudioContext is suspended or closed. State = ',
      audioContext.state
    );
    return;
  }

  let inputData = e.inputBuffer.getChannelData(0);

  // The samples are floats in range [-1, 1]. Convert to PCM16le.
  let output = new DataView(new ArrayBuffer(inputData.length * 2));
  for (let i = 0; i < inputData.length; i++) {
    let multiplier = inputData[i] < 0 ? 0x8000 : 0x7fff; // 16-bit signed range is -32768 to 32767
    output.setInt16(i * 2, (inputData[i] * multiplier) | 0, true); // index, value, little edian
  }

  let intData = new Int16Array(output.buffer);
  let index = intData.length;

  if (socket.connected) {
    socket.emit(
      'audio',
      JSON.stringify({
        timestamp: new Date().toISOString(),
        audio: Object.values(intData.slice(0, index + 1)),
      })
    );
    setMicLevel(calculateMicLevelPercent(inputData));
  } else {
    setMicLevel('offline');
  }
}

function onOpen(
  setMicLevel,
  selectedMicrophoneId,
  successCallback,
  failureCallback
) {
  navigator.mediaDevices
    .getUserMedia({
      audio: selectedMicrophoneId ? { deviceId: selectedMicrophoneId } : true,
    })
    .catch(() => {
      return navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    })
    .then((stream) => {
      micStream = stream;
      return audioContext.resume().then(() => {
        scriptNode = audioContext.createScriptProcessor(4096, 1, 1);
        input = audioContext.createMediaStreamSource(micStream);
        scriptNode.addEventListener('audioprocess', (event) =>
          processAudioEvent(event, setMicLevel, failureCallback)
        );
        input.connect(scriptNode);
        scriptNode.connect(audioContext.destination);
        audioContext.resume();

        window.isMicOn = true;
        successCallback();
      });
    })
    .catch((error) => {
      console.error('Error accessing microphone:', error);
      failureCallback('MICROPHONE_SETUP_FAILED');
    });
}
